import { AuthUserPermission, OrgUserModel } from '@cibo/core'
import { PaginatedFieldsProvider } from '@cibo/landmanager'
import { Can, Loading, useUser } from '@cibo/profile'
import {
  MarkdownListItem,
  MarkdownUnorderedListContainer,
  ResponsiveDialog,
  intuitiveFromNow,
} from '@cibo/ui'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Breadcrumbs,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import {
  AgreementListRequest,
  AgreementStatus,
  AgreementStatusChip,
  AgreementStatusUpdateModal,
  AttachmentsTable,
  DataValidationDetail,
  ErrorReportDetail,
  OfferDetail,
  SoilHealthDetail,
  useAgreement,
  useDownloadAgreement,
} from 'features/Agreements'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RRLink, useParams } from 'react-router-dom'

const agreementDetailComponents = {
  dataCollection: DataValidationDetail,
  offer: OfferDetail,
  adminReview: ErrorReportDetail,
  soilHealth: SoilHealthDetail,
}

export const AgreementDetail = ({
  agreementId: propAgreementId,
  variant,
}: Pick<AgreementListRequest, 'agreementId'> & { variant: 'user' | 'agreements' }) => {
  const {
    palette: { error },
  } = useTheme()
  const { agreementId: paramAgreementId = '' } = useParams()
  const agreementId = propAgreementId || paramAgreementId
  const { type = 'data-validation' } = useParams()
  const { t } = useTranslation('impact/pages/Agreements')
  const [errorMessage, setErrorMessage] = useState<string>()
  const onError = async ({ response }: AxiosError) => {
    const isJsonBlob = (data: unknown) => data instanceof Blob && data.type === 'application/json'
    const responseData = isJsonBlob(response?.data)
      ? await response?.data?.text()
      : response?.data || {}
    const responseJson = typeof responseData === 'string' ? JSON.parse(responseData) : responseData
    setErrorMessage && setErrorMessage(responseJson.message ? responseJson.message : 'other')
  }
  const agreement = useAgreement(agreementId)
  const [fileDownloaded, setFileDownloaded] = useState<boolean>(false)
  const { openFile, downloading } = useDownloadAgreement(onError, () => setFileDownloaded(true))
  const user = useUser()
  const [actionModal, setActionModal] = useState<{ open: boolean; action?: AgreementStatus }>({
    open: false,
    action: undefined,
  })

  const downloadButton = useMemo(
    () =>
      !!agreement?.data?.docPath ? (
        <LoadingButton
          data-testid="download-agreement-file"
          onClick={openFile(agreement.data)}
          loading={downloading}
          variant="contained"
          color="secondary"
          size="small"
          endIcon={<DownloadRoundedIcon />}
          title={t('download-title', { fileName: agreement.data?.fileName })}
        >
          {t('download', { context: type })}
        </LoadingButton>
      ) : null,
    [agreement.dataUpdatedAt]
  )
  const markdownAComponentProps = useMemo(
    () =>
      !!agreement?.data?.docPath
        ? {
            onClick: openFile(agreement.data),
            color: 'secondary',
            sx: { display: 'inline', cursor: 'pointer' },
          }
        : {},
    [agreement.dataUpdatedAt]
  )

  const detailProps = useMemo(
    () => ({
      markdownOverrides: {
        p: {
          component: Typography,
          props: { paragraph: true },
        },
        a: {
          component: Typography,
          props: markdownAComponentProps,
        },
        li: {
          component: MarkdownListItem,
          props: {
            sx: { typography: 'body1', marginBottom: '8px' },
          },
        },
        ul: {
          component: MarkdownUnorderedListContainer,
          props: { sx: { marginBottom: '10px', typography: 'body1' } },
        },
      },
      downloadButton,
      setActionModal,
      agreementId,
      fileDownloaded,
    }),
    [agreement.isFetched, fileDownloaded]
  )

  if (agreement.isLoading || user.isLoading) {
    return <Loading />
  }

  const agreementYear = dayjs(agreement.data?.createdDate).format('YYYY')
  const handleCloseModal = () => setActionModal({ open: false, action: undefined })

  const agreementOwner = agreement.data ? new OrgUserModel(agreement.data?.user) : undefined

  const AgreementDetailComponent =
    agreement.data?.agreementType && agreementDetailComponents[agreement.data.agreementType]

  return (
    <PaginatedFieldsProvider>
      <Stack spacing={4} display="flex">
        <Stack spacing={2} alignItems="flex-start">
          <Breadcrumbs>
            <Link
              component={RRLink}
              to={'../'}
              underline="hover"
              color="inherit"
              data-testid={`agreement-${type}`}
            >
              {variant === 'user' ? t('agreements') : t('routeBreadcrumb', { context: type })}
            </Link>
            {variant === 'agreements' && type !== 'soil-health' && (
              <Link
                component={RRLink}
                to={`..?t=${agreement.data?.status}`}
                underline="hover"
                color="inherit"
              >
                {t(`${agreement.data?.status}`)}
              </Link>
            )}
            {!!agreement.data && (
              <Link
                component={RRLink}
                underline="hover"
                color="text.primary"
                to={'.'}
                aria-current="page"
              >
                {variant === 'agreements' && agreementOwner
                  ? agreementOwner.displayNameFull
                  : t('agreementBreadcrumb', { context: agreement.data.agreementType })}{' '}
                - {agreementYear}
              </Link>
            )}
          </Breadcrumbs>

          <Stack spacing={1} direction="row" alignItems="center">
            {!!agreement.data?.status && (
              <AgreementStatusChip agreement={agreement.data} size="small" />
            )}

            <Typography color="text.secondary" variant="caption">
              {t('createdDate', {
                date: agreement.data?.createdDate
                  ? intuitiveFromNow(agreement.data?.createdDate)
                  : '',
              })}
            </Typography>
          </Stack>
        </Stack>

        {!AgreementDetailComponent ? (
          <Typography>{t('couldNotFind')}</Typography>
        ) : (
          <AgreementDetailComponent {...detailProps} />
        )}

        <Can useAny={[AuthUserPermission.AGREEMENTS_ATTACHMENTS_READ]}>
          {!!agreement.data?.agreementId && (
            <AttachmentsTable agreementId={agreement.data.agreementId} />
          )}
        </Can>
      </Stack>

      <AgreementStatusUpdateModal
        {...actionModal}
        handleCloseModal={handleCloseModal}
        agreementId={agreementId}
        agreementYear={agreementYear}
      />

      <ResponsiveDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage(undefined)}
        maxWidth="sm"
        fullWidth
      >
        <Box style={{ width: '100%', height: 8, backgroundColor: error.main }} />
        <DialogTitle color="error">{t('downloadErrorDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('downloadErrorDialogBody')}</Typography>
          <Can useAll={[AuthUserPermission.DEBUG_USER]}>
            <Box flex={1} style={{ overflow: 'auto' }}>
              <pre>{errorMessage}</pre>
            </Box>
          </Can>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMessage(undefined)}>{t('close')}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </PaginatedFieldsProvider>
  )
}
